import { useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import moment from 'moment-wrapper'

import colors from 'components/utils/colors'
import Badge from 'components/Shared/Badge/Index'
import Icon from 'components/Shared/Icon/Index'
import Tooltip from 'components/Shared/Tooltip/Index'

import * as sessionUserSelector from 'selectors/sessionUser'


const YearHeader = styled.div`
  height: 40px;
  background-color: ${colors.secondary95};
  display: flex;
  align-items: center;
  border-bottom: solid 1px ${colors.secondary70};
  cursor: pointer;
  padding-left: 16px;
`

const Year = styled.span`
  font-family: Exo;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${colors.secondary20};
  padding-right: 4px;
`

const ReleasesSection = styled.div`
  background-color: ${colors.secondary90};
`

const StyledReleaseDate = styled.div`
`

const StyledReleaseVersion = styled.div`
  margin-right: 5px;
`

const StyledReleaseItem = styled.div`
  font-size: 14px;
  display: flex;
  line-height: 38px;
  align-items: center;
  cursor: pointer;
  padding-left: 16px;
  padding-right: 40px;
  color: ${colors.secondary20};
  border-bottom: solid 1px ${colors.secondary70};
  background-color: ${props => props.active ? colors.primary80 : 'transparent'};
  white-space: nowrap;
  flex-flow: wrap;

  &:hover {
    background-color: ${colors.primary90};
  }
`

const StyledReleaseBadge = styled(Badge)`
  position: absolute;
  right: 0;
  margin-right: 16px;
  padding 0 6px;
`

const releaseDateToString = (date) => {
  return moment(date).tz('UTC').format('MMM DD, YYYY')
}

const ReleaseBadge = ({ release }) => {
  const { t } = useTranslation()
  const releaseIconName = release.published ? 'icon-published-12' : 'icon-edit-12'
  const releaseIconTooltip = release.published ? t('releaseNotesPage.publishedTooltip') : t('releaseNotesPage.unpublishedTooltip')
  const badgeColor = release.published ? colors.success73 : colors.warning80

  return (
    <StyledReleaseBadge
      color={badgeColor}
      margin='0'
      data-tooltip-content={releaseIconTooltip}
      data-tooltip-id='release-info'
    >
      <Icon iconName={releaseIconName} />
    </StyledReleaseBadge>
  )
}

ReleaseBadge.propTypes = {
  release: PropTypes.object,
}

const ReleaseList = ({ releases, selectedReleaseNoteId, year, onReleaseNoteClick }) => {
  const [isExpanded, setIsExpanded] = useState(true)

  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const expandIcon = isExpanded ? 'icon-arrow-down-small-8' : 'icon-arrow-right-small-8'

  return (
    <>
      <YearHeader data-test='year' onClick={toggleExpanded}>
        <Year>{year}</Year>
        <Icon iconName={expandIcon} />
      </YearHeader>
      {isExpanded && (
        <ReleasesSection>
          {releases.map(release =>
            <StyledReleaseItem
              key={release.id}
              active={selectedReleaseNoteId === release.id}
              onClick={() => onReleaseNoteClick(release)}
              data-test='release-note'
            >
              <StyledReleaseVersion>{release.version}</StyledReleaseVersion>
              <StyledReleaseDate>{releaseDateToString(release.date)}</StyledReleaseDate>
              {userIsAdmin && <ReleaseBadge release={release} />}
            </StyledReleaseItem>
          )}
        </ReleasesSection>
      )}
      <Tooltip id='release-info' />
    </>
  )
}

ReleaseList.propTypes = {
  releases: PropTypes.array.isRequired,
  selectedReleaseNoteId: PropTypes.string,
  year: PropTypes.string,
  onReleaseNoteClick: PropTypes.func.isRequired,
}

export default ReleaseList
